import { useCallback, useEffect, useState } from 'react';
import { api, guardarToken, tokenActual, type Perfil } from './api';
import Layout, { type Vista } from './components/Layout';
import { Cargando, PestanasEtiquetas } from './components/ui';
import { useLatido, VIVO } from './lib/useApi';
import Login from './views/Login';
import Dashboard from './views/Dashboard';
import Stock from './views/Stock';
import Solicitud from './views/Solicitud';
import Fraccionamiento from './views/Fraccionamiento';
import Racks from './views/Racks';
import DatosContenedores from './views/DatosContenedores';
import Ubicar from './views/Ubicar';
import Operar from './views/Operar';
import EtiquetasUbicacion from './views/EtiquetasUbicacion';
import EtiquetasPallet from './views/EtiquetasPallet';
import Estadias from './views/Estadias';
import CargarInventario from './views/CargarInventario';
import NuevoContenedor from './views/NuevoContenedor';
import Armado from './views/Armado';
import ResumenDiario from './views/ResumenDiario';
import PruebaFacturacion from './views/PruebaFacturacion';
import Usuarios from './views/Usuarios';
import Planta from './views/planta/Planta';
import Etiquetas from './views/Etiquetas';
import EtiquetaManual from './views/EtiquetaManual';
import Mantenimiento from './views/Mantenimiento';
import Articulos from './views/Articulos';
import Archivos from './views/Archivos';
import PedidosMail from './views/PedidosMail';
import RolloEtiquetas from './components/RolloEtiquetas';
import { anotarRollo } from './lib/rollo';
import Remitos from './views/Remitos';
import Entrega from './views/Entrega';
import PalletsEliminados from './views/PalletsEliminados';
import FichaContenedor from './views/FichaContenedor';
import VaciarRack from './views/VaciarRack';
import { ProveedorSesion } from './lib/sesion';
import { tokenDeEntrega } from './lib/entrega';
import { HUSO } from './theme';

const VISTAS: Vista[] = [
  'dashboard', 'resumen', 'stock', 'contenedores', 'solicitud', 'armado', 'frac', 'correo', 'racks', 'ubicar', 'operar', 'etiquetasUbicacion', 'etiquetasPallet', 'estadias', 'inventario', 'articulos', 'archivos', 'planta', 'etiquetas', 'manual', 'remitos', 'mantenimiento', 'usuarios', 'facturacion', 'papeles', 'palletsEliminados', 'contenedor', 'vaciarRack',
];

type Ruta = { vista: Vista; id: string | null };

/**
 * Ruteo por hash: `#/frac` es el listado y `#/frac/PLT-004821` el detalle de
 * ese pallet. Así el operario puede recargar o compartir el link de lo que
 * está mirando sin perder el contexto.
 */
function rutaDeHash(): Ruta {
  const [vista, id] = window.location.hash.replace(/^#\/?/, '').split('/');
  // Tracking y Recepción se sacaron del menú: los links viejos (notificaciones
  // guardadas, marcadores) caen en Nuevo contenedor en vez del dashboard.
  if (vista === 'tracking' || vista === 'recepcion') return { vista: 'contenedores', id: null };
  if (!vista || !VISTAS.includes(vista as Vista)) return { vista: 'dashboard', id: null };
  return { vista: vista as Vista, id: id ? decodeURIComponent(id) : null };
}

export default function App() {
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [verificando, setVerificando] = useState(Boolean(tokenActual()));
  const [ruta, setRuta] = useState<Ruta>(rutaDeHash);
  const [badges, setBadges] = useState<Partial<Record<Vista, number>>>({});
  const [sincronizado, setSincronizado] = useState('—');

  // Sesión guardada: se revalida contra el backend antes de mostrar el sistema.
  useEffect(() => {
    if (!tokenActual()) return;
    let vigente = true;
    api
      .yo()
      .then(({ usuario }) => vigente && setPerfil(usuario))
      .catch(() => guardarToken(null))
      .finally(() => vigente && setVerificando(false));
    return () => {
      vigente = false;
    };
  }, []);

  useEffect(() => {
    const alCambiar = () => setRuta(rutaDeHash());
    window.addEventListener('hashchange', alCambiar);
    return () => window.removeEventListener('hashchange', alCambiar);
  }, []);

  const irA = useCallback((vista: Vista, id?: string) => {
    // Notificaciones viejas en la base todavía traen vista 'tracking'/'recepcion'.
    const destino: Ruta =
      (vista as string) === 'tracking' || (vista as string) === 'recepcion'
        ? { vista: 'contenedores', id: null }
        : { vista, id: id ?? null };
    setRuta(destino);
    window.location.hash = destino.id ? `#/${destino.vista}/${encodeURIComponent(destino.id)}` : `#/${destino.vista}`;
  }, []);

  // Los contadores de la navegación salen de los mismos datos que el dashboard,
  // y se rehacen solos: son el aviso de que hay trabajo nuevo, así que valen
  // poco si hay que recargar la página para verlos.
  const latido = useLatido(VIVO.normal);

  useEffect(() => {
    if (!perfil) return;
    let vigente = true;
    // Stock y Permanencia no suman acá: son totales informativos (pallets en
    // depósito, 2ª quincena de cobro), no trabajo pendiente.
    api
      .dashboard()
      .then((d) => {
        if (!vigente) return;
        setBadges({
          solicitud: d.otAbiertas,
          contenedores: d.contenedoresEnTransito,
          armado: d.otEnArmado,
          // Lo pendiente de la lista de órdenes: las que siguen abiertas.
          frac: d.otAbiertas,
          correo: d.pedidosMail,
        });
        setSincronizado(
          new Date(d.sincronizado).toLocaleString('es-AR', {
            timeZone: HUSO,
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }),
        );
      })
      .catch(() => {});
    // Cuántas le quedan al rollo de la térmica, a mano para cualquier pantalla
    // que imprima: la ventana de impresión no puede esperar a preguntarlo.
    api
      .ajustes()
      .then((a) => vigente && anotarRollo(a.rolloEtiquetas))
      .catch(() => {});
    return () => {
      vigente = false;
    };
  }, [perfil, ruta.vista, ruta.id, latido]);

  function salir() {
    // Si el servidor no contesta, igual se sale: el token local se borra.
    api.salir().catch(() => {});
    guardarToken(null);
    setPerfil(null);
    irA('dashboard');
  }

  // Antes que la sesión: quien abre un enlace de entrega no tiene usuario en el
  // sistema, y quien sí lo tiene igual quiere ver la pantalla del enlace cuando
  // lo abre para probarlo.
  const entrega = tokenDeEntrega(window.location.hash);
  if (entrega) return <Entrega token={entrega} />;

  if (verificando) return <Cargando texto="Recuperando tu sesión…" alto={320} />;
  if (!perfil) return <Login onEntrar={setPerfil} />;

  // Guardarraíl: si el rol no alcanza para la vista pedida —por una URL vieja o
  // escrita a mano— cae en la suya. El servidor rechaza igual cada llamada.
  const permitida = perfil.vistas.includes(ruta.vista);
  const vista = permitida ? ruta.vista : (perfil.vistaInicial as Vista);
  const id = permitida ? ruta.id : null;
  // La ficha de un pallet es «Fraccionamiento» para cualquiera, pero abierta
  // en uno puntual conviene decir cuál: el título de la cabecera lo refleja.
  const titulo =
    vista === 'frac' && id ? `${/^OT-/i.test(id) ? 'Orden' : 'Pallet'} ${id}` : vista === 'contenedor' && id ? `Contenedor ${id}` : undefined;

  return (
    <ProveedorSesion perfil={perfil}>
    <Layout
      vista={vista}
      titulo={titulo}
      onVista={irA}
      perfil={perfil}
      onSalir={salir}
      badges={badges}
      sincronizado={sincronizado}
    >
      {vista === 'dashboard' && <Dashboard onVista={irA} />}
      {/* Desde el stock se abre la ficha del pallet, que es donde está su historial. */}
      {vista === 'stock' && (
        <Stock onVerPallet={(pallet) => irA('frac', pallet)} puedeUbicar={perfil.vistas.includes('ubicar')} />
      )}
      {vista === 'contenedores' && (
        <NuevoContenedor
          onVerTodos={() => irA('papeles')}
          onVerArmado={() => irA('armado')}
          onUbicar={() => irA('ubicar')}
          onVerContenedor={(ct) => irA('contenedor', ct)}
        />
      )}
      {vista === 'resumen' && <ResumenDiario />}
      {vista === 'facturacion' && <PruebaFacturacion />}
      {vista === 'solicitud' && <Solicitud onVerStock={() => irA('stock')} onVerOrden={(id) => irA('frac', id)} />}
      {/* Un solo camino para ubicar pallets: la vista Ubicar pallets. */}
      {vista === 'armado' && <Armado onUbicar={(pallet) => irA('ubicar', pallet)} onVerContenedor={(ct) => irA('contenedor', ct)} />}
      {vista === 'frac' && (
        <Fraccionamiento pallet={id} onAbrir={(pallet) => irA('frac', pallet)} onVolver={() => irA('frac')} onIr={irA} />
      )}
      {vista === 'racks' && <Racks busquedaInicial={id} onVaciarRack={perfil.vistas.includes('vaciarRack') ? (rack) => irA('vaciarRack', rack) : undefined} onUbicar={(pallet?: string) => irA('ubicar', pallet)} onVerPallet={(pallet) => irA('frac', pallet)} />}
      {vista === 'papeles' && <DatosContenedores onVerFicha={(ct) => irA('contenedor', ct)} onNuevo={() => irA('contenedores')} />}
      {vista === 'contenedor' && <FichaContenedor id={id} onIr={irA} />}
      {vista === 'ubicar' && <Ubicar pallet={id ?? undefined} />}
      {/* `#/operar/mover~PLT-…`: lo escaneado arriba. Se usa una vez y se saca del link, así recargar no lo repite. */}
      {vista === 'operar' && (
        <Operar
          onIr={irA}
          inicio={id}
          onInicioLeido={() => {
            window.history.replaceState(null, '', '#/operar');
            setRuta({ vista: 'operar', id: null });
          }}
        />
      )}
      {/* Las tres etiquetas son una sola entrada del menú: las pestañas van acá para no tocar las vistas. */}
      {(vista === 'etiquetasUbicacion' || vista === 'etiquetasPallet' || vista === 'manual') && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <PestanasEtiquetas actual={vista} vistas={perfil.vistas} onVista={irA} />
          <RolloEtiquetas />
        </div>
      )}
      {vista === 'etiquetasUbicacion' && <EtiquetasUbicacion />}
      {vista === 'etiquetasPallet' && <EtiquetasPallet contenedor={id} onVerContenedor={(ct) => irA('contenedor', ct)} />}
      {vista === 'estadias' && <Estadias onIr={irA} />}
      {vista === 'inventario' && <CargarInventario onListo={() => setRuta({ ...ruta })} />}
      {vista === 'planta' && <Planta />}
      {vista === 'etiquetas' && <Etiquetas />}
      {vista === 'manual' && <EtiquetaManual />}
      {vista === 'remitos' && <Remitos destino={id} />}
      {vista === 'mantenimiento' && <Mantenimiento />}
      {vista === 'articulos' && <Articulos />}
      {vista === 'archivos' && <Archivos />}
      {vista === 'correo' && <PedidosMail id={id} onAbrir={(p) => irA('correo', p)} onVolver={() => irA('correo')} onVerOrden={(ot) => irA('frac', ot)} />}
      {vista === 'usuarios' && <Usuarios />}
      {vista === 'palletsEliminados' && <PalletsEliminados onVerContenedor={(ct) => irA('contenedor', ct)} />}
      {vista === 'vaciarRack' && <VaciarRack rackInicial={id} onIr={irA} />}
    </Layout>
    </ProveedorSesion>
  );
}
